import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { CART_LOAD_SUCCESS } from "~/features/cart/cart/redux/actions";
import { buildAlternateRowId } from "~/features/cart/cartV2/utils/buildAlternateRowId";
import type { AlternateRowViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";

export interface StoredAlternateRow {
  anchorCartEntryId: number | null;
  productCode: string;
  designation: string;
  order: number;
}

export interface CartAlternatesState {
  rowsByTableId: Record<string, StoredAlternateRow[]>;
  nextOrderByTableId: Record<string, number>;
}

const initialState: CartAlternatesState = {
  rowsByTableId: {},
  nextOrderByTableId: {},
};

export interface InsertAlternateRowPayload {
  tableId: string;
  anchorCartEntryId: number | null;
  productCode: string;
  designation: string;
}

export interface CloseAlternateRowPayload {
  tableId: string;
  rowId: string;
  entryIds: number[];
}

export interface NormalizeTableRowsPayload {
  tableId: string;
  entryIds: number[];
}

function buildStoredRowIdentity(anchorCartEntryId: number | null, productCode: string): string {
  const anchorKey = anchorCartEntryId === null ? "root" : String(anchorCartEntryId);
  return `${anchorKey}:${productCode}`;
}

function dedupeStoredRows(storedRows: StoredAlternateRow[]): StoredAlternateRow[] {
  const rowsByIdentity = new Map<string, StoredAlternateRow>();

  storedRows.forEach((storedRow) => {
    const rowIdentity = buildStoredRowIdentity(storedRow.anchorCartEntryId, storedRow.productCode);
    const existingRow = rowsByIdentity.get(rowIdentity);

    if (existingRow && existingRow.order <= storedRow.order) {
      return;
    }

    rowsByIdentity.set(rowIdentity, storedRow);
  });

  return Array.from(rowsByIdentity.values()).sort((left, right) => left.order - right.order);
}

export function normalizeStoredRows(storedRows: StoredAlternateRow[], entryIds: number[]): StoredAlternateRow[] {
  const validAnchorIds = new Set(entryIds);

  if (entryIds.length === 0) {
    return dedupeStoredRows(storedRows);
  }

  return dedupeStoredRows(
    storedRows.filter(
      (storedRow) => storedRow.anchorCartEntryId !== null && validAnchorIds.has(storedRow.anchorCartEntryId),
    ),
  );
}

function normalizeAnchorCartEntryId(
  anchorCartEntryId: number | null,
  validAnchorIds: Set<number>,
  preserveStoredAnchors: boolean,
): number | null | undefined {
  if (preserveStoredAnchors) {
    return anchorCartEntryId;
  }

  if (anchorCartEntryId !== null && validAnchorIds.has(anchorCartEntryId)) {
    return anchorCartEntryId;
  }

  return undefined;
}

export function buildAlternateRows(args: {
  tableId: string;
  storedRows: StoredAlternateRow[];
  entryIds: number[];
}): AlternateRowViewModel[] {
  const { tableId, storedRows, entryIds } = args;
  const validAnchorIds = new Set(entryIds);
  const preserveStoredAnchors = entryIds.length === 0;
  const rowsByIdentity = new Map<string, AlternateRowViewModel & { order: number }>();

  storedRows.forEach((storedRow) => {
    const normalizedAnchorCartEntryId = normalizeAnchorCartEntryId(
      storedRow.anchorCartEntryId,
      validAnchorIds,
      preserveStoredAnchors,
    );

    if (normalizedAnchorCartEntryId === undefined) {
      return;
    }

    const rowId = buildAlternateRowId({
      tableId,
      anchorCartEntryId: normalizedAnchorCartEntryId,
      productCode: storedRow.productCode,
    });
    const existingRow = rowsByIdentity.get(rowId);

    if (existingRow && existingRow.order <= storedRow.order) {
      return;
    }

    rowsByIdentity.set(rowId, {
      kind: "alternate",
      rowId,
      tableId,
      anchorCartEntryId: normalizedAnchorCartEntryId,
      productCode: storedRow.productCode,
      designation: storedRow.designation,
      order: storedRow.order,
    });
  });

  return Array.from(rowsByIdentity.values())
    .sort((left, right) => left.order - right.order)
    .map(({ order: _order, ...row }) => row);
}

const cartAlternatesSlice = createSlice({
  name: "cartAlternates",
  initialState,
  reducers: {
    insertAlternateRow(state, action: PayloadAction<InsertAlternateRowPayload>) {
      const { tableId, anchorCartEntryId, productCode, designation } = action.payload;
      const currentRows = state.rowsByTableId[tableId] ?? [];

      const alreadyExists = currentRows.some(
        (row) => row.anchorCartEntryId === anchorCartEntryId && row.productCode === productCode,
      );

      if (alreadyExists) {
        return;
      }

      const nextOrder = state.nextOrderByTableId[tableId] ?? 0;
      state.rowsByTableId[tableId] = [
        ...currentRows,
        { anchorCartEntryId, productCode, designation, order: nextOrder },
      ];
      state.nextOrderByTableId[tableId] = nextOrder + 1;
    },

    closeAlternateRow(state, action: PayloadAction<CloseAlternateRowPayload>) {
      const { tableId, rowId, entryIds } = action.payload;
      const currentRows = state.rowsByTableId[tableId] ?? [];

      const builtRows = buildAlternateRows({ tableId, storedRows: currentRows, entryIds });
      const targetRow = builtRows.find((row) => row.rowId === rowId);

      if (!targetRow) {
        return;
      }

      const validAnchorIds = new Set(entryIds);
      state.rowsByTableId[tableId] = currentRows.filter((row) => {
        const normalizedAnchorId = normalizeAnchorCartEntryId(
          row.anchorCartEntryId,
          validAnchorIds,
          entryIds.length === 0,
        );
        return !(normalizedAnchorId === targetRow.anchorCartEntryId && row.productCode === targetRow.productCode);
      });
    },

    normalizeTableRows(state, action: PayloadAction<NormalizeTableRowsPayload>) {
      const { tableId, entryIds } = action.payload;
      const currentRows = state.rowsByTableId[tableId] ?? [];
      const nextRows = normalizeStoredRows(currentRows, entryIds);
      state.rowsByTableId[tableId] = nextRows;
    },

    removeTable(state, action: PayloadAction<string>) {
      delete state.rowsByTableId[action.payload];
      delete state.nextOrderByTableId[action.payload];
    },

    clearAllAlternates(state) {
      state.rowsByTableId = {};
      state.nextOrderByTableId = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(CART_LOAD_SUCCESS, (state) => {
      state.rowsByTableId = {};
      state.nextOrderByTableId = {};
    });
  },
});

export const { insertAlternateRow, closeAlternateRow, normalizeTableRows, removeTable, clearAllAlternates } =
  cartAlternatesSlice.actions;

export default cartAlternatesSlice.reducer;
